import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useTranslation } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useApi } from '../hooks/useApi';
import { useToast } from '../components/Shared/Toast';

/**
 * BerechtigungenPage - Block 3.
 *
 * Editierbare Tool-Scope-Matrix (Nutzer x Tool -> tool_role) je Mandant.
 * Backend: GET/PUT /api/platform/admin/tenants/{id}/tool-scopes
 * (Persistenz ueber PlatformUserToolAccess). platform_admin darf setzen,
 * org_admin sieht read-only. Hat ein Nutzer noch keine expliziten Scopes,
 * wird die Platform-Rolle als Fallback projiziert (markiert), bis explizit gesetzt.
 */

const ROLE_COLORS = {
  provider_admin: '#8b5cf6',
  tenant_admin: '#6366f1',
  lead_auditor: '#0891b2',
  auditor: '#10b981',
  viewer: '#9ca3af',
};

const cardStyle = {
  background: 'var(--bg-card)', border: '1px solid var(--border)',
  borderRadius: 10, padding: 32, textAlign: 'center',
  color: 'var(--text-muted)', fontSize: 13,
};

export default function BerechtigungenPage() {
  const { t } = useTranslation();
  const { userRole } = useAuth();
  const { api } = useApi();
  const { showToast } = useToast();

  const isPlatformAdmin = userRole === 'platform_admin';

  const [tenants, setTenants] = useState([]);
  const [selectedTenantId, setSelectedTenantId] = useState('');
  const [data, setData] = useState(null); // { tools, users, valid_roles, editable }
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  // draft: key `${userId}::${toolId}` -> role string ('' = entfernen)
  const [draft, setDraft] = useState({});

  useEffect(() => {
    api('admin/tenants')
      .then(d => setTenants(Array.isArray(d) ? d : (d?.items || d?.tenants || [])))
      .catch(err => showToast(t('common.error') + ': ' + (err?.message || ''), 'error'));
  }, [api, showToast, t]);

  const loadScopes = useCallback((tid) => {
    if (!tid) { setData(null); return; }
    setLoading(true);
    setDraft({});
    api(`admin/tenants/${tid}/tool-scopes`)
      .then(setData)
      .catch(err => showToast(t('common.error') + ': ' + (err?.message || ''), 'error'))
      .finally(() => setLoading(false));
  }, [api, showToast, t]);

  useEffect(() => { loadScopes(selectedTenantId); }, [selectedTenantId, loadScopes]);

  // aktuelle (explizite) Rolle je user/tool
  const currentRole = useMemo(() => {
    const m = {};
    (data?.users || []).forEach(u => {
      m[u.id] = {};
      (u.tool_scopes || []).forEach(s => {
        if (!s.projected) m[u.id][s.tool_id] = s.tool_role;
      });
    });
    return m;
  }, [data]);

  const projectedRole = useMemo(() => {
    const m = {};
    (data?.users || []).forEach(u => {
      m[u.id] = {};
      (u.tool_scopes || []).forEach(s => {
        if (s.projected) m[u.id][s.tool_id] = s.tool_role;
      });
    });
    return m;
  }, [data]);

  const editable = isPlatformAdmin && data?.editable;
  const dirty = Object.keys(draft).length > 0;

  const cellValue = (uid, tid) => {
    const k = `${uid}::${tid}`;
    if (k in draft) return draft[k];
    return currentRole[uid]?.[tid] || '';
  };

  const setCell = (uid, tid, value) => {
    const k = `${uid}::${tid}`;
    const original = currentRole[uid]?.[tid] || '';
    setDraft(prev => {
      const next = { ...prev };
      if (value === original) delete next[k];
      else next[k] = value;
      return next;
    });
  };

  const save = async () => {
    const changes = Object.entries(draft).map(([k, role]) => {
      const [user_id, tool_id] = k.split('::');
      return { user_id, tool_id, tool_role: role === '' ? null : role };
    });
    if (changes.length === 0) return;
    setSaving(true);
    try {
      const res = await api(`admin/tenants/${selectedTenantId}/tool-scopes`, {
        method: 'PUT', body: { changes },
      });
      showToast(t('admin.permissions.saved', 'Gespeichert') + ` (${res?.applied ?? changes.length})`, 'success');
      loadScopes(selectedTenantId);
    } catch (err) {
      showToast(t('common.error') + ': ' + (err?.message || ''), 'error');
    } finally {
      setSaving(false);
    }
  };

  const roleOptions = data?.valid_roles || Object.keys(ROLE_COLORS);

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 16, fontWeight: 700 }}>{t('admin.permissions.title')}</div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
          {t('admin.permissions.subtitle')}
        </div>
      </div>

      {/* Mandanten-Picker + Save */}
      <div style={{
        background: 'var(--bg-card)', border: '1px solid var(--border)',
        borderRadius: 10, padding: 16, marginBottom: 16,
        display: 'flex', gap: 12, alignItems: 'flex-end',
      }}>
        <div style={{ flex: 1, maxWidth: 360 }}>
          <label className="field-label">{t('admin.permissions.select_tenant')}</label>
          <select className="field-input" value={selectedTenantId}
            onChange={e => setSelectedTenantId(e.target.value)}>
            <option value="">—</option>
            {tenants.map(tn => (
              <option key={tn.id} value={tn.id}>{tn.name} ({tn.slug})</option>
            ))}
          </select>
        </div>
        {editable && (
          <button className="btn btn-primary" disabled={!dirty || saving} onClick={save}>
            {saving ? t('admin.permissions.saving', 'Speichere…') : t('admin.permissions.save', 'Aenderungen speichern')}
          </button>
        )}
      </div>

      {/* Info-Banner */}
      <div style={{
        background: 'rgba(91,106,240,.12)', border: '1px solid rgba(91,106,240,.3)',
        borderRadius: 8, padding: '10px 14px', fontSize: 12,
        color: 'var(--text-muted)', marginBottom: 16,
      }}>
        {editable
          ? t('admin.permissions.info_edit')
          : t('admin.permissions.info_readonly')}
      </div>

      {!selectedTenantId ? (
        <div style={cardStyle}>{t('admin.permissions.no_tenant')}</div>
      ) : loading ? (
        <div style={cardStyle}>{t('admin.permissions.loading')}</div>
      ) : !data || (data.users || []).length === 0 ? (
        <div style={cardStyle}>{t('admin.permissions.no_users', 'Keine Nutzer.')}</div>
      ) : (
        <div style={{
          background: 'var(--bg-card)', border: '1px solid var(--border)',
          borderRadius: 10, overflow: 'hidden',
        }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: 'var(--bg2, var(--bg))' }}>
                  <th style={{ textAlign: 'left', padding: '10px 12px', borderBottom: '1px solid var(--border)' }}>
                    {t('admin.permissions.user_column', 'Nutzer')}
                  </th>
                  {(data.tools || []).map(tool => (
                    <th key={tool.id} style={{ textAlign: 'center', padding: '10px 12px', borderBottom: '1px solid var(--border)' }}>
                      {tool.label || tool.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(data.users || []).map(u => (
                  <tr key={u.id}>
                    <td style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)' }}>
                      <div style={{ fontWeight: 600 }}>{u.display_name}</div>
                      <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>
                        {u.username}
                        {!u.has_explicit && (
                          <span style={{ marginLeft: 6, fontStyle: 'italic' }}>
                            · {t('admin.permissions.inherited', 'Fallback aus Rolle')}
                          </span>
                        )}
                      </div>
                    </td>
                    {(data.tools || []).map(tool => {
                      const val = cellValue(u.id, tool.id);
                      const proj = projectedRole[u.id]?.[tool.id];
                      if (editable) {
                        return (
                          <td key={tool.id} style={{ padding: '6px 10px', textAlign: 'center', borderBottom: '1px solid var(--border)' }}>
                            <select
                              className="field-input"
                              style={{ fontSize: 11, padding: '4px 6px', minWidth: 120 }}
                              value={val}
                              onChange={e => setCell(u.id, tool.id, e.target.value)}
                            >
                              <option value="">
                                {proj ? `— (${t('admin.permissions.fallback', 'Fallback')}: ${proj})` : t('admin.permissions.no_scope', '— kein Zugang')}
                              </option>
                              {roleOptions.map(r => (
                                <option key={r} value={r}>{t(`shared.tool_scope.role.${r}`, r)}</option>
                              ))}
                            </select>
                          </td>
                        );
                      }
                      const shown = val || proj;
                      const color = ROLE_COLORS[shown] || '#9ca3af';
                      return (
                        <td key={tool.id} style={{ padding: '8px 12px', textAlign: 'center', borderBottom: '1px solid var(--border)' }}>
                          {shown ? (
                            <span style={{
                              display: 'inline-block', padding: '2px 8px', borderRadius: 10,
                              fontSize: 11, fontWeight: 600, color: '#fff', background: color,
                              opacity: val ? 1 : 0.55,
                            }}>
                              {t(`shared.tool_scope.role.${shown}`, shown)}
                            </span>
                          ) : (
                            <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>—</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
